import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("sdf_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      const requestUrl = err?.config?.url || "";
      // Don't redirect on the login endpoint itself — the caller wants the
      // 401 so it can render an inline error under the form.
      const isLoginCall = /\/auth\/(login|retailer\/verify-widget-token)/.test(requestUrl);
      if (!isLoginCall) {
        localStorage.removeItem("sdf_token");
        localStorage.removeItem("sdf_user");
        const path = window.location.pathname || "";
        const ADMIN_LOGIN_PATH = process.env.REACT_APP_ADMIN_LOGIN_PATH || "/secure-admin-login";
        const ADMIN_DASHBOARD_PATH = process.env.REACT_APP_ADMIN_DASHBOARD_PATH || "/secure-admin";
        // Send expired super-admin sessions back to their hidden login, not
        // the public retailer login. Everything else → retailer /login.
        const isSuperArea = path.startsWith(ADMIN_DASHBOARD_PATH) || path.startsWith(ADMIN_LOGIN_PATH);
        const dest = isSuperArea ? ADMIN_LOGIN_PATH : "/login";
        // Don't force a redirect if we're already on a login page or on
        // /admin (which is the employee login gate — AdminGate will re-render
        // to show the login form as soon as the token is gone).
        const onLoginSurface = path.startsWith("/login") || path.startsWith(ADMIN_LOGIN_PATH) || path.startsWith("/admin");
        if (!onLoginSurface) {
          window.location.href = dest;
        }
      }
    }
    return Promise.reject(err);
  }
);

// ------------------------------------------------------------------
// Client-side image optimisation
// ------------------------------------------------------------------
// Keeps images sharp — only downsizes if larger than maxDim and only re-encodes
// if it results in a smaller file. Skips small (<200 KB) images entirely.
async function compressImage(file, { maxDim = 1800, quality = 0.9 } = {}) {
  if (!file || !file.type || !file.type.startsWith("image/")) return file;
  if (file.size < 200 * 1024) return file;
  let bitmap;
  try { bitmap = await createImageBitmap(file); } catch { return file; }
  const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
  const w = Math.round(bitmap.width * scale);
  const h = Math.round(bitmap.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(bitmap, 0, 0, w, h);
  bitmap.close && bitmap.close();
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
  if (!blob || blob.size >= file.size) return file;
  const name = (file.name || "upload").replace(/\.(png|heic|heif|webp|jpeg|jpg)$/i, "") + ".jpg";
  return new File([blob], name, { type: "image/jpeg", lastModified: Date.now() });
}

const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"];
const MAX_UPLOAD_BYTES = 50 * 1024 * 1024; // 50 MB after compression (videos larger, images will be smaller after compress)

// Prevent double-clicks / concurrent uploads for the same file identity.
const inflightUploads = new Map();

// ------------------------------------------------------------------
// Signed direct upload to Cloudinary
// ------------------------------------------------------------------
async function _uploadToCloudinary(file, resourceType, onProgress) {
  // Ask backend for a fresh signature — API secret never touches the client.
  // Preview environments occasionally see HTTP/2 head-of-line blocking when many
  // legacy /api/files/* images are in-flight, so we allow a generous timeout
  // plus one retry on network abort.
  let sig;
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const { data } = await api.get(
        `/admin/cloudinary/signature?resource_type=${resourceType}`,
        { timeout: 30000 },
      );
      sig = data;
      break;
    } catch (err) {
      const aborted = err?.code === "ECONNABORTED" || err?.message?.includes("timeout");
      if (aborted && attempt === 0) continue;   // one retry
      throw new Error(aborted ? "Server is busy, please try again" : (err?.response?.data?.detail || "Could not start upload"));
    }
  }

  const form = new FormData();
  form.append("file", file);
  form.append("api_key", sig.api_key);
  form.append("timestamp", sig.timestamp);
  form.append("signature", sig.signature);
  form.append("folder", sig.folder);

  const endpoint = `https://api.cloudinary.com/v1_1/${sig.cloud_name}/${resourceType}/upload`;

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", endpoint);
    xhr.timeout = resourceType === "video" ? 300000 : 120000; // 5 min video / 2 min image
    xhr.upload.onprogress = (e) => {
      if (onProgress && e.lengthComputable) {
        onProgress(Math.round((e.loaded * 100) / e.total));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const r = JSON.parse(xhr.responseText);
          resolve({
            url: r.secure_url,
            public_id: r.public_id,
            width: r.width,
            height: r.height,
            bytes: r.bytes,
            format: r.format,
            resource_type: r.resource_type,
          });
        } catch (err) { reject(new Error("Invalid Cloudinary response")); }
      } else {
        let msg = "Upload failed";
        try { msg = JSON.parse(xhr.responseText).error?.message || msg; } catch (_) {}
        reject(new Error(msg));
      }
    };
    xhr.onerror = () => reject(new Error("Network error — check your internet"));
    xhr.ontimeout = () => reject(new Error("Upload timed out — please try again"));
    xhr.onabort = () => reject(new Error("Upload cancelled"));
    xhr.send(form);
  });
}

/**
 * Public upload helper.
 * Returns { url, public_id, width, height, bytes, format }.
 * Applies compression, prevents duplicate concurrent uploads of the same file,
 * and always resolves/rejects (never hangs).
 */
export async function uploadFile(file, onProgress) {
  if (!file) throw new Error("No file selected");
  const isImage = file.type?.startsWith("image/");
  const isVideo = file.type?.startsWith("video/");
  if (!isImage && !isVideo) throw new Error("Only image or video files are allowed");
  if (isImage && !ALLOWED_TYPES.includes(file.type)) {
    // Still permit unknown image mimetypes to try; Cloudinary auto-detects.
  }
  const key = `${file.name}|${file.size}|${file.lastModified}`;
  if (inflightUploads.has(key)) return inflightUploads.get(key);

  const task = (async () => {
    const uploadable = isImage ? await compressImage(file) : file;
    if (uploadable.size > MAX_UPLOAD_BYTES) {
      throw new Error(`File is too large (${(uploadable.size / 1024 / 1024).toFixed(1)} MB). Max 50 MB.`);
    }
    return _uploadToCloudinary(uploadable, isVideo ? "video" : "image", onProgress);
  })().finally(() => inflightUploads.delete(key));

  inflightUploads.set(key, task);
  return task;
}

/**
 * Warm the Cloudinary signature call. Safe to call multiple times — the
 * response is short-lived (60s) but this at least kicks the request in
 * parallel with user actions, hiding preview-origin HTTP/2 head-of-line
 * blocking from legacy /api/files/* image proxying.
 */
export function prewarmUploadSignature(resourceType = "image") {
  // fire-and-forget; ignore result (each real upload still fetches a fresh sig)
  api.get(`/admin/cloudinary/signature?resource_type=${resourceType}`, { timeout: 30000 })
    .catch(() => { /* silently absorb — real upload will retry */ });
}

/**
 * Ask the backend to delete a Cloudinary asset. Safe to call with a legacy or
 * unknown public_id — backend swallows errors and returns { ok: false }.
 */
export async function deleteCloudinaryAsset(publicId) {
  if (!publicId) return { ok: false };
  try {
    const { data } = await api.post("/admin/cloudinary/delete", { public_id: publicId }, { timeout: 15000 });
    return data;
  } catch (err) {
    return { ok: false, error: err?.response?.data?.detail || err.message };
  }
}

export default api;
